// ===== ArtifactPanel (Phase 3, T4 artifact-substrate) =====
// 우측 패널 4상태 기계(빈/동영상/아티팩트-위-동영상/아티팩트)를 단독 소유한다.
// 상태는 어디에도 저장하지 않고 매 조회 시 (패널 open 여부, 동영상 재생 여부)로부터 계산한다 —
// 저장된 상태와 실제 DOM이 어긋나는 동기화 버그를 원천 차단한다.
// AvatarController/CanvasVideoRenderer/agent-cards.js(_updateRightPanelVisibility)는 절대
// 참조/수정하지 않는다 — 동영상 재생 여부는 그들이 이미 토글해 둔 .avatar-panel--has-active
// 클래스를 읽기만 한다. ax-detail-sidepanel 계열 열기/닫기 idiom은 업무 보드 상세
// (views/work/board.ts _boardOpenDetailSidepanel/_boardCloseDetailSidepanel)와 같은 모양이다.

type ArtifactPanelArtifact = { id: string; display: string; kind?: string; title: string; payload?: { path?: string; url?: string; content?: string } };
// Phase light(room-file-drawer 핫픽스): 파일 서랍이 같은 패널을 미리보기 창으로 재사용한다.
// 아티팩트가 아니라 FileRecord의 공개 projection(routes/files.ts)이므로 별도 형상으로 받는다.
type ArtifactPanelFile = { id: string; displayName: string; mediaType: string; contentUrl: string; size: number };
type ArtifactPanelFileKind = "markdown" | "text" | "csv" | "download";
// 고정 근거(pinned rail)가 메시지 원문을 같은 패널에서 보여준다. 서버 projectPinnedItemSource 가
// 내려주는 메시지 projection 형상이며, 아티팩트/파일과 배타적인 세 번째 모드를 이룬다.
type ArtifactPanelMessage = { seq?: number; content?: string; timestamp?: string; senderName?: string | null };
type ArtifactPanelMode = "artifact" | "file" | "message";
type ArtifactPanelOpenOptions = { triggerEl?: HTMLElement | null; onJump?: (() => void) | null };
type ArtifactPanelDom = { root: HTMLElement | null; panel: HTMLElement | null; backdrop: HTMLElement | null; closeBtn: HTMLElement | null; type: HTMLElement | null; title: HTMLElement | null; body: HTMLElement | null; actionsToolbar: HTMLElement | null; sendToPluginBtn: HTMLElement | null; transferBtn: HTMLElement | null; downloadBtn: HTMLAnchorElement | null; gotoBtn: HTMLElement | null; resizer: HTMLElement | null };
type ArtifactPanelAction = { plugin: string; label: string };
// Phase 5 (bigtionax-chat-item-transfer): item-transfer-ui.js가 정의하는 room picker — 부재 시
// (로드 순서/미배선) close()의 closePicker 훅이 no-op으로 안전 폴백하도록 optional로만 선언한다.
interface Window {
  ArtifactPanel: {
    open: (artifact: unknown, triggerEl?: HTMLElement | null, options?: ArtifactPanelOpenOptions) => void;
    openFile: (file: unknown, options?: { siblings?: unknown; triggerEl?: HTMLElement | null }) => void;
    // 메시지 원문 미리보기. onJump 를 주면 '대화에서 보기' 액션이 나타난다 — 패널은 타임라인을
    // 알지 못하고 호출부(ChatTimelineSurfaces)가 넘긴 콜백만 실행한다.
    openMessage: (message: unknown, options?: ArtifactPanelOpenOptions) => void;
    // 호출부가 "이 파일을 상세 패널에서 열 만한가"를 자체 판정하지 않게 하는 단일 창구.
    // 이미지·PDF는 MediaViewerLauncher 소관이므로 여기서는 Markdown·CSV만 인정한다.
    canPreview: (file: unknown) => boolean;
    close: () => void;
    getState: () => string;
  };
  ItemTransfer?: { openPicker: (anchorEl: HTMLElement, ctx: unknown) => void; closePicker: () => void };
}
function isArtifactPanelRecord(value: unknown): value is Record<string, unknown> { return typeof value === "object" && value !== null && !Array.isArray(value); }
function isArtifactPanelArtifact(value: unknown): value is ArtifactPanelArtifact {
  return isArtifactPanelRecord(value) && typeof value.id === "string" && typeof value.display === "string" && typeof value.title === "string" &&
    (value.kind === undefined || typeof value.kind === "string") && (value.payload === undefined || isArtifactPanelRecord(value.payload));
}
function isArtifactPanelMessage(value: unknown): value is ArtifactPanelMessage {
  return isArtifactPanelRecord(value)
    && (value.content === undefined || typeof value.content === "string")
    && (value.senderName === undefined || value.senderName === null || typeof value.senderName === "string")
    && (value.timestamp === undefined || typeof value.timestamp === "string")
    && (value.seq === undefined || typeof value.seq === "number");
}
function isArtifactPanelFile(value: unknown): value is ArtifactPanelFile {
  return isArtifactPanelRecord(value) && typeof value.id === "string" && typeof value.displayName === "string" &&
    typeof value.mediaType === "string" && typeof value.contentUrl === "string" &&
    typeof value.size === "number" && isFinite(value.size);
}
function isArtifactPanelActionList(value: unknown): value is ArtifactPanelAction[] {
  return Array.isArray(value) && value.every(function (item) {
    return isArtifactPanelRecord(item) && typeof item.plugin === "string" && typeof item.label === "string";
  });
}

(function () {
  var _dom: ArtifactPanelDom | null = null;
  var _lastTrigger: HTMLElement | null = null;
  var _currentArtifact: ArtifactPanelArtifact | null = null;
  var _actionsPopoverOpen = false;
  var _jumpHandler: (() => void) | null = null;

  function _apDom(): ArtifactPanelDom {
    if (_dom) return _dom;
    _dom = {
      root: document.getElementById("artifact-detail-sidepanel-root"),
      panel: document.getElementById("artifact-detail-sidepanel"),
      backdrop: document.getElementById("artifact-detail-sidepanel-backdrop"),
      closeBtn: document.getElementById("artifact-btn-close-detail-sidepanel"),
      type: document.getElementById("artifact-detail-sidepanel-type"),
      title: document.getElementById("artifact-detail-sidepanel-title"),
      body: document.getElementById("artifact-detail-sidepanel-body"),
      actionsToolbar: document.getElementById("artifact-detail-sidepanel-actions"),
      sendToPluginBtn: document.getElementById("artifact-btn-send-to-plugin"),
      transferBtn: document.getElementById("artifact-btn-transfer"),
      downloadBtn: document.getElementById("artifact-btn-download-file") as HTMLAnchorElement | null,
      gotoBtn: document.getElementById("artifact-btn-goto-message"),
      resizer: document.getElementById("artifact-detail-sidepanel-resizer"),
    };
    if (_dom.gotoBtn) _dom.gotoBtn.addEventListener("click", _onGotoClick);
    if (_dom.closeBtn) _dom.closeBtn.addEventListener("click", close);
    if (_dom.backdrop) _dom.backdrop.addEventListener("click", close);
    if (_dom.sendToPluginBtn) _dom.sendToPluginBtn.addEventListener("click", _onSendToPluginClick);
    if (_dom.transferBtn) _dom.transferBtn.addEventListener("click", _onTransferClick);
    document.addEventListener("keydown", function (event) {
      const dom = _dom;
      if (event.key === "Escape" && dom?.root?.dataset.open === "true") close();
    });
    return _dom;
  }

  // ===== 플러그인으로 보내기 (Phase 3, T5 artifact-plugin-action) =====
  // REST fetch만 사용 — WS 이벤트 타입을 추가하지 않는다(MESSAGE_HANDLERS 불변).

  function _apActionsUrl(artifactId: string): string {
    return "/api/artifacts/" + encodeURIComponent(artifactId) + "/actions";
  }

  function _apSendToUrl(artifactId: string, plugin: string): string {
    return "/api/artifacts/" + encodeURIComponent(artifactId) + "/send-to/" + encodeURIComponent(plugin);
  }

  async function _apFetchJson(url: string, options?: RequestInit): Promise<Record<string, unknown>> {
    var response = await fetch(url, options);
    var payload: unknown = await response.json().catch(function () { return {}; });
    if (!isArtifactPanelRecord(payload)) throw new Error("Invalid JSON response");
    if (!response.ok || payload.ok === false) {
      throw new Error(typeof payload.error === "string" ? payload.error : "Request failed: " + response.status);
    }
    return payload;
  }

  function _apErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }

  function _actionsPopoverEl(): HTMLElement | null {
    return document.getElementById("artifact-actions-popover");
  }

  function _onActionsOutsideClick(event: MouseEvent): void {
    var pop = _actionsPopoverEl();
    var dom = _apDom();
    if (pop && event.target instanceof Node && pop.contains(event.target)) return;
    if (dom.sendToPluginBtn && event.target instanceof Node && dom.sendToPluginBtn.contains(event.target)) return;
    _closeActionsPopover();
  }

  function _closeActionsPopover(): void {
    var pop = _actionsPopoverEl();
    if (pop) pop.remove();
    _actionsPopoverOpen = false;
    document.removeEventListener("click", _onActionsOutsideClick, true);
  }

  function _renderActionsPopover(actions: ArtifactPanelAction[]): void {
    var dom = _apDom();
    if (!dom.actionsToolbar) return;
    _closeActionsPopover();

    var pop = document.createElement("div");
    pop.id = "artifact-actions-popover";
    pop.className = "artifact-panel-actions-popover";

    if (!actions.length) {
      pop.innerHTML = '<div class="artifact-panel-actions-empty">이 아티팩트로 실행할 액션이 없습니다</div>';
    } else {
      pop.innerHTML = actions.map(function (action) {
        return '<button type="button" class="artifact-panel-actions-item" data-plugin="' + escapeHtml(action.plugin) + '">' +
          escapeHtml(action.label) + '</button>';
      }).join("");
      pop.querySelectorAll<HTMLButtonElement>(".artifact-panel-actions-item").forEach(function (btn) {
        btn.addEventListener("click", function () {
          if (btn.dataset.plugin) _sendToPlugin(btn.dataset.plugin);
        });
      });
    }

    dom.actionsToolbar.appendChild(pop);
    _actionsPopoverOpen = true;
    setTimeout(function () {
      document.addEventListener("click", _onActionsOutsideClick, true);
    }, 0);
  }

  function _setSendToPluginLoading(loading: boolean): void {
    var dom = _apDom();
    if (!dom.sendToPluginBtn) return;
    dom.sendToPluginBtn.toggleAttribute("disabled", loading);
    dom.sendToPluginBtn.textContent = loading ? "불러오는 중…" : "플러그인으로 보내기";
  }

  // ===== 다른 방에 전달 (Phase 5, T6 bigtionax-chat-item-transfer) =====
  // room picker 자체는 item-transfer-ui.js가 단독 소유한다 — 여기서는 상호배타(플러그인 팝오버
  // 닫기)를 지키고 원본 artifact record를 ctx로 넘기기만 한다(getCurrentArtifact 공개 API 미신설).
  function _onTransferClick(): void {
    _closeActionsPopover();
    if (!_currentArtifact) return;
    var dom = _apDom();
    if (!dom.transferBtn) return;
    window.ItemTransfer?.openPicker(dom.transferBtn, { artifact: _currentArtifact });
  }

  async function _onSendToPluginClick(): Promise<void> {
    if (_actionsPopoverOpen) {
      _closeActionsPopover();
      return;
    }
    if (!_currentArtifact) return;
    var artifactId = _currentArtifact.id;

    _setSendToPluginLoading(true);
    try {
      var data = await _apFetchJson(_apActionsUrl(artifactId));
      _renderActionsPopover(isArtifactPanelActionList(data.actions) ? data.actions : []);
    } catch (error) {
      if (typeof showSummaryToast === "function") showSummaryToast("액션 목록을 불러오지 못했어요: " + _apErrorMessage(error), true);
    } finally {
      _setSendToPluginLoading(false);
    }
  }

  // 실패 시 팝오버를 닫지 않는다 — 같은 항목을 다시 눌러 재시도할 수 있게 둔다.
  async function _sendToPlugin(plugin: string): Promise<void> {
    if (!_currentArtifact) return;
    var artifactId = _currentArtifact.id;
    try {
      await _apFetchJson(_apSendToUrl(artifactId, plugin), { method: "POST" });
      _closeActionsPopover();
      if (typeof showSummaryToast === "function") showSummaryToast("플러그인으로 보냈어요", true);
    } catch (error) {
      if (typeof showSummaryToast === "function") showSummaryToast("전송에 실패했어요: " + _apErrorMessage(error) + " — 다시 시도해 주세요", true);
    }
  }

  // 읽기 전용 입력: 아바타 우측 패널이 이미 토글해 둔 클래스만 본다.
  function _hasActiveVideo(): boolean {
    return document.querySelector(".avatar-panel--has-active") !== null;
  }

  function _isOpen(): boolean {
    var dom = _apDom();
    return !!(dom.root && dom.root.dataset.open === "true");
  }

  function getState(): string {
    var hasActive = _hasActiveVideo();
    if (_isOpen()) return hasActive ? "artifact-over-video" : "artifact-only";
    return hasActive ? "video-playing" : "empty";
  }

  // ===== Display별 본문 렌더 =====

  function _renderVideo(artifact: ArtifactPanelArtifact): string {
    var src = (artifact.payload && (artifact.payload.path || artifact.payload.url)) || "";
    return '<video class="artifact-panel-video" controls preload="metadata" src="' + escapeHtml(src) + '"></video>';
  }

  function _renderFile(artifact: ArtifactPanelArtifact): string {
    var href = (artifact.payload && (artifact.payload.path || artifact.payload.url)) || "#";
    return '<a class="artifact-panel-file" href="' + escapeHtml(href) + '" download target="_blank" rel="noopener">📎 ' + escapeHtml(artifact.title) + ' 다운로드</a>';
  }

  function _renderLink(artifact: ArtifactPanelArtifact): string {
    var href = (artifact.payload && (artifact.payload.path || artifact.payload.url)) || "#";
    return '<a class="artifact-panel-link" href="' + escapeHtml(href) + '" target="_blank" rel="noopener">🔗 ' + escapeHtml(artifact.title) + '</a>';
  }

  function _renderMarkdownArtifact(artifact: ArtifactPanelArtifact): string {
    var content = (artifact.payload && artifact.payload.content) || "";
    var rendered = typeof renderMarkdown === "function" ? renderMarkdown(content) : escapeHtml(content);
    return '<div class="artifact-panel-markdown markdown-body">' + rendered + '</div>';
  }

  var ARTIFACT_PANEL_RENDERERS: Record<string, (artifact: ArtifactPanelArtifact) => string> = {
    "video": _renderVideo,
    "file": _renderFile,
    "link": _renderLink,
    "markdown": _renderMarkdownArtifact,
  };

  // ===== 파일 미리보기 모드 (room file drawer 재사용) =====
  // 패널 DOM·열기/닫기·ESC·포커스 복귀는 아티팩트 모드와 그대로 공유하고, 본문 렌더러와
  // 액션 줄만 갈린다(아티팩트 액션은 숨기고 다운로드를 노출).
  //
  // 본문은 innerHTML 문자열 조립 대신 DOM 노드로 만든다 — 파일명이 alt/title 같은 속성
  // 컨텍스트에 들어가는데 markdown.ts의 escapeHtml은 따옴표를 인코딩하지 않기 때문이다
  // (views/chat.ts:104-108의 escapeHtmlAttr 주석과 같은 이유). 마크다운 본문만 예외로
  // renderMarkdown(DOMPurify 통과) 결과를 innerHTML 로 넣는다.
  var MAX_TEXT_PREVIEW_BYTES = 1024 * 1024;
  var MAX_CSV_ROWS = 200;
  var MAX_CSV_COLUMNS = 30;
  var FILE_KIND_LABEL: Record<ArtifactPanelFileKind, string> = {
    markdown: "📝 마크다운",
    text: "📄 텍스트",
    csv: "📊 CSV",
    download: "📎 파일",
  };
  var _fileToken = 0;

  function _fileExtension(displayName: string): string {
    var match = /\.([A-Za-z0-9]+)$/.exec(displayName);
    return match ? match[1].toLowerCase() : "";
  }

  // 업로드 시 mimetype 이 비면 application/octet-stream 으로 굳으므로(routes/files.ts)
  // mediaType 단독으로는 종류를 못 정한다 — views/chat.ts 첨부 렌더와 같은 확장자 폴백을 쓴다.
  function _fileKind(file: ArtifactPanelFile): ArtifactPanelFileKind {
    var mediaType = (file.mediaType || "").toLowerCase();
    var extension = _fileExtension(file.displayName);
    if (mediaType === "text/markdown" || extension === "md") return "markdown";
    if (mediaType === "text/plain" || extension === "txt") return "text";
    if (mediaType === "text/csv" || extension === "csv") return "csv";
    return "download";
  }

  function canPreview(file: unknown): boolean {
    return isArtifactPanelFile(file) && _fileKind(file) !== "download";
  }

  // 액션 줄은 모드마다 배타적이다: 아티팩트 액션은 artifact 에서만(파일/메시지 id로는
  // /api/artifacts 액션이 성립하지 않는다), 다운로드는 file 에서만, '대화에서 보기'는 호출부가
  // onJump 를 넘긴 경우에만 노출한다.
  function _setPanelMode(mode: ArtifactPanelMode): void {
    var dom = _apDom();
    if (dom.sendToPluginBtn) dom.sendToPluginBtn.hidden = mode !== "artifact";
    if (dom.transferBtn) dom.transferBtn.hidden = mode !== "artifact";
    if (dom.downloadBtn) dom.downloadBtn.hidden = mode !== "file";
    if (dom.gotoBtn) dom.gotoBtn.hidden = !_jumpHandler;
  }

  function _onGotoClick(): void {
    var jump = _jumpHandler;
    close();
    if (jump) jump();
  }

  function _fileStateNode(text: string, kind?: string): HTMLElement {
    var node = document.createElement("p");
    node.className = "artifact-panel-file-state";
    node.textContent = text;
    if (kind) node.dataset.kind = kind;
    return node;
  }

  // RFC 4180 형상만 다룬다: 쉼표 구분, 큰따옴표로 감싼 필드, 그 안의 "" 이스케이프, CRLF/LF.
  // maxRows 에 도달하면 즉시 멈춘다 — 2GB 업로드 상한(routes/files.ts) 아래에서 전량 파싱은
  // 미리보기가 감당할 작업이 아니다.
  function _parseCsv(text: string, maxRows: number): { rows: string[][]; truncated: boolean } {
    var source = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
    var rows: string[][] = [];
    var row: string[] = [];
    var field = "";
    var quoted = false;
    var index = 0;
    while (index < source.length) {
      var character = source.charAt(index);
      if (quoted) {
        if (character === '"') {
          if (source.charAt(index + 1) === '"') { field += '"'; index += 2; continue; }
          quoted = false; index += 1; continue;
        }
        field += character; index += 1; continue;
      }
      if (character === '"') { quoted = true; index += 1; continue; }
      if (character === ",") { row.push(field); field = ""; index += 1; continue; }
      if (character === "\n" || character === "\r") {
        if (character === "\r" && source.charAt(index + 1) === "\n") index += 1;
        row.push(field); field = ""; rows.push(row); row = [];
        index += 1;
        if (rows.length >= maxRows) return { rows: rows, truncated: index < source.length };
        continue;
      }
      field += character; index += 1;
    }
    if (field !== "" || row.length) { row.push(field); rows.push(row); }
    return { rows: rows, truncated: false };
  }

  function _renderCsvTable(text: string): HTMLElement {
    var parsed = _parseCsv(text, MAX_CSV_ROWS + 1);
    var rows = parsed.rows;
    if (!rows.length) return _fileStateNode("표로 읽을 내용이 없어요.");

    var columns = Math.min(MAX_CSV_COLUMNS, rows.reduce(function (widest, current) {
      return Math.max(widest, current.length);
    }, 0));
    var table = document.createElement("table");
    var head = document.createElement("thead");
    var headRow = document.createElement("tr");
    for (var headIndex = 0; headIndex < columns; headIndex += 1) {
      var th = document.createElement("th");
      var headText = rows[0][headIndex] || "";
      th.textContent = headText;
      th.title = headText;
      headRow.appendChild(th);
    }
    head.appendChild(headRow);
    table.appendChild(head);

    var body = document.createElement("tbody");
    for (var rowIndex = 1; rowIndex < rows.length; rowIndex += 1) {
      var tableRow = document.createElement("tr");
      for (var cellIndex = 0; cellIndex < columns; cellIndex += 1) {
        var td = document.createElement("td");
        var cellText = rows[rowIndex][cellIndex] || "";
        td.textContent = cellText;
        td.title = cellText;
        tableRow.appendChild(td);
      }
      body.appendChild(tableRow);
    }
    table.appendChild(body);

    var scroller = document.createElement("div");
    scroller.className = "artifact-panel-csv";
    scroller.appendChild(table);

    var wrapper = document.createElement("div");
    wrapper.appendChild(scroller);
    if (parsed.truncated) {
      var note = document.createElement("p");
      note.className = "artifact-panel-csv-note";
      note.textContent = "앞 " + MAX_CSV_ROWS + "행만 보여주고 있어요. 전체는 다운로드로 확인해 주세요.";
      wrapper.appendChild(note);
    }
    return wrapper;
  }

  async function _loadFileBody(file: ArtifactPanelFile, kind: ArtifactPanelFileKind, token: number): Promise<void> {
    var dom = _apDom();
    try {
      if (file.size > MAX_TEXT_PREVIEW_BYTES) {
        if (token === _fileToken && dom.body) {
          dom.body.replaceChildren(_fileStateNode("미리보기에는 너무 큰 파일이에요. 아래 다운로드로 받아 주세요."));
        }
        return;
      }
      var response = await fetch(file.contentUrl, { credentials: "same-origin" });
      if (!response.ok) throw new Error("HTTP " + response.status);

      var text = await response.text();
      if (token !== _fileToken || !dom.body) return;
      if (kind === "csv") {
        dom.body.replaceChildren(_renderCsvTable(text));
        return;
      }
      if (kind === "text") {
        var plain = document.createElement("pre");
        plain.className = "artifact-panel-plain-text";
        plain.textContent = text;
        dom.body.replaceChildren(plain);
        return;
      }
      var article = document.createElement("div");
      article.className = "artifact-panel-markdown markdown-body";
      article.innerHTML = typeof renderMarkdown === "function" ? renderMarkdown(text) : escapeHtml(text);
      dom.body.replaceChildren(article);
    } catch (_error: unknown) {
      if (token === _fileToken && dom.body) {
        dom.body.replaceChildren(_fileStateNode("파일을 불러오지 못했어요. 아래 다운로드로 받아 주세요.", "error"));
      }
    }
  }

  function _showFile(file: ArtifactPanelFile): void {
    var dom = _apDom();
    if (!file || !dom.body || !dom.type || !dom.title) return;
    var token = ++_fileToken;
    var kind = _fileKind(file);

    dom.type.textContent = FILE_KIND_LABEL[kind];
    dom.title.textContent = file.displayName;
    if (dom.downloadBtn) {
      dom.downloadBtn.href = file.contentUrl;
      dom.downloadBtn.setAttribute("download", file.displayName);
    }

    if (kind === "download") {
      dom.body.replaceChildren(_fileStateNode("이 형식은 미리보기를 지원하지 않아요. 아래 다운로드로 받아 주세요."));
      return;
    }
    dom.body.replaceChildren(_fileStateNode("불러오는 중…"));
    void _loadFileBody(file, kind, token);
  }

  function openFile(file: unknown, options?: { siblings?: unknown; triggerEl?: HTMLElement | null }): void {
    if (!isArtifactPanelFile(file)) return;
    var siblings = options && Array.isArray(options.siblings)
      ? options.siblings.filter(isArtifactPanelFile)
      : undefined;
    if (window.MediaViewerLauncher?.openFile(file, siblings)) return;
    var dom = _apDom();
    if (!dom.root || !dom.body || !dom.type || !dom.title || !dom.panel) return;
    _lastTrigger = (options && options.triggerEl) || (document.activeElement instanceof HTMLElement ? document.activeElement : null);

    _ensurePanelResizer();
    _closeActionsPopover();
    _currentArtifact = null;
    _jumpHandler = null;
    _setPanelMode("file");
    _showFile(file);

    dom.root.hidden = false;
    dom.root.dataset.open = "true";
    dom.panel.setAttribute("aria-hidden", "false");
    document.body.classList.add("ax-detail-sidepanel-open");
  }

  // ===== 패널 폭 조절 =====
  // 서랍과 같은 utils/panel-resizer를 쓴다 — md 본문이나 CSV 표는 520px 고정으로는 좁다.
  // 패널은 화면 전체를 덮는 fixed root 안에 있으므로 가용 폭 기준은 뷰포트다.
  var _panelResizer: PanelResizer | null = null;

  function _ensurePanelResizer(): void {
    if (_panelResizer) return;
    var dom = _apDom();
    if (!dom.resizer || !dom.root || typeof attachPanelResizer !== "function") return;
    _panelResizer = attachPanelResizer({
      handle: dom.resizer,
      target: dom.root,
      cssVariable: "--artifact-panel-width",
      storageKey: "life-os-artifact-panel-width",
      defaultWidth: 520,
      minWidth: 320,
      maxWidth: 1040,
      // 뒤 화면이 완전히 가려지면 닫을 곳(백드롭)을 잡을 수 없다.
      minRemaining: 160,
      measureAvailable: () => window.innerWidth || 0,
    });
  }

  // ===== Open / Close =====

  function open(artifact: unknown, triggerEl?: HTMLElement | null, options?: ArtifactPanelOpenOptions): void {
    if (!isArtifactPanelArtifact(artifact)) return;
    if (window.MediaViewerLauncher?.openArtifact(artifact)) return;
    var dom = _apDom();
    if (!dom.root || !dom.body || !dom.type || !dom.title || !dom.panel) return;
    _lastTrigger = triggerEl || (options && options.triggerEl) || (document.activeElement instanceof HTMLElement ? document.activeElement : null);

    _ensurePanelResizer();
    // 직전에 파일 미리보기로 열렸을 수 있으므로 아티팩트 모드 액션 줄을 되돌린다.
    _fileToken += 1;
    _jumpHandler = (options && options.onJump) || null;
    _setPanelMode("artifact");

    var renderer = ARTIFACT_PANEL_RENDERERS[artifact.display] || _renderFile;
    dom.body.innerHTML = renderer(artifact);
    dom.type.textContent = "🗂️ 아티팩트";
    dom.title.textContent = artifact.title || "";
    _closeActionsPopover();
    _currentArtifact = artifact; // 액션(플러그인으로 보내기)이 참조하는 열린 아티팩트 상태

    dom.root.hidden = false;
    dom.root.dataset.open = "true";
    dom.panel.setAttribute("aria-hidden", "false");
    document.body.classList.add("ax-detail-sidepanel-open");
  }

  // 패널은 대화 위에 뜬 모달이다 — 같은 탭으로 이동하면 보던 맥락을 잃는다. 앱이 직접 만드는
  // 링크의 기존 규약(target=_blank + rel=noopener, views/chat.ts:139)을 렌더 결과에도 맞춘다.
  // sanitize 이후 DOM 에서 속성만 얹는다: ALLOWED_ATTR 에 target 을 추가하면 마크다운을 쓰는
  // 다른 화면(말풍선·문서·루틴)까지 한꺼번에 바뀐다.
  function _openLinksInNewTab(root: HTMLElement): void {
    for (const anchor of Array.from(root.querySelectorAll("a[href]"))) {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    }
  }

  // 메시지 원문은 말풍선과 같은 렌더러(renderMarkdown = marked + DOMPurify)를 탄다 — 같은 문장이
  // 타임라인에서는 서식으로, 패널에서는 원문 기호로 보이면 다른 내용처럼 읽힌다.
  function openMessage(message: unknown, options?: ArtifactPanelOpenOptions): void {
    if (!isArtifactPanelMessage(message)) return;
    var dom = _apDom();
    if (!dom.root || !dom.body || !dom.type || !dom.title || !dom.panel) return;
    _lastTrigger = (options && options.triggerEl) || (document.activeElement instanceof HTMLElement ? document.activeElement : null);

    _ensurePanelResizer();
    _fileToken += 1;
    _closeActionsPopover();
    _currentArtifact = null;
    _jumpHandler = (options && options.onJump) || null;
    _setPanelMode("message");

    var body = document.createElement("div");
    body.className = "artifact-panel-message";
    if (message.timestamp) {
      var meta = document.createElement("p");
      meta.className = "artifact-panel-message-meta";
      meta.textContent = message.timestamp;
      body.appendChild(meta);
    }
    var text = document.createElement("div");
    // markdown-body 는 말풍선이 쓰는 것과 같은 서식 클래스다.
    text.className = "artifact-panel-message-text artifact-panel-markdown markdown-body";
    var content = message.content || "";
    text.innerHTML = typeof renderMarkdown === "function" ? renderMarkdown(content) : escapeHtml(content);
    _openLinksInNewTab(text);
    body.appendChild(text);
    dom.body.replaceChildren(body);

    dom.type.textContent = "\u{1F4AC} 메시지";
    dom.title.textContent = message.senderName || "메시지";

    dom.root.hidden = false;
    dom.root.dataset.open = "true";
    dom.panel.setAttribute("aria-hidden", "false");
    document.body.classList.add("ax-detail-sidepanel-open");
  }

  function close(): void {
    var dom = _apDom();
    if (!dom.root || !dom.panel || dom.root.dataset.open !== "true") return;

    _closeActionsPopover();
    window.ItemTransfer?.closePicker?.();
    _currentArtifact = null;
    // 열려 있던 blob URL은 닫는 즉시 회수한다 — 방을 옮겨다니며 PDF를 여닫는 동안
    // 회수되지 않은 blob이 탭 수명 내내 쌓이는 걸 막는다.
    _fileToken += 1;
    _jumpHandler = null;
    _setPanelMode("artifact");
    dom.root.dataset.open = "false";
    dom.panel.setAttribute("aria-hidden", "true");
    document.body.classList.remove("ax-detail-sidepanel-open");

    var returnFocusTarget = _lastTrigger;
    const root = dom.root;
    window.setTimeout(function () {
      if (root.dataset.open === "false") {
        root.hidden = true;
        if (returnFocusTarget && typeof returnFocusTarget.focus === "function" && document.contains(returnFocusTarget)) {
          returnFocusTarget.focus();
        }
      }
    }, 220);
    // 밑의 아바타 canvas 루프는 열려 있는 동안 건드린 적이 없으므로 복귀는 자동이다 —
    // avatar API(AvatarController/CanvasVideoRenderer) 호출 없이 getState()가 다음 조회 시
    // video-playing/empty를 그대로 계산해낸다.
  }

  window.ArtifactPanel = { open: open, openFile: openFile, openMessage: openMessage, canPreview: canPreview, close: close, getState: getState };
})();
